import { v4 as uuidv4 } from "uuid";
import { firebaseAuth } from "@/shared/lib/firebase";
import { env } from "@/shared/lib/env";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ResolvedUrl {
  readonly fullUrl: string;
  /**
   * Whether the resolved URL targets our trusted API origin. The Firebase
   * Bearer token and the X-Tenant-Id header are ONLY attached to requests
   * whose final host matches `VITE_API_BASE_URL`. Any other host gets neither,
   * so a token/tenant can never leak to a CDN or attacker-controlled callback.
   */
  readonly isTrustedOrigin: boolean;
}

function resolveUrl(url: string): ResolvedUrl {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, "");
  const trustedOrigin = new URL(base).origin;

  if (/^https?:\/\//i.test(url)) {
    const parsed = new URL(url);
    return { fullUrl: parsed.toString(), isTrustedOrigin: parsed.origin === trustedOrigin };
  }

  const path = url.startsWith("/") ? url : `/${url}`;
  return { fullUrl: `${base}${path}`, isTrustedOrigin: true };
}

/**
 * Normalize any HeadersInit shape into a flat lowercase map. HTTP header names
 * are case-insensitive on the wire, so lowercasing lets framework headers
 * override caller headers cleanly.
 */
function normalizeHeaders(input: HeadersInit | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input) return out;
  new Headers(input).forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}

async function getAuthHeader(
  isTrustedOrigin: boolean,
  forceRefresh = false,
): Promise<Record<string, string>> {
  if (!isTrustedOrigin) return {};
  const user = firebaseAuth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken(forceRefresh);
    return { authorization: `Bearer ${token}` };
  } catch (err) {
    throw new Error("Failed to retrieve Firebase ID token for request", { cause: err });
  }
}

/** A body is replayable if the same RequestInit can be sent to fetch twice. */
function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body == null) return true;
  if (typeof body === "string") return true;
  if (body instanceof FormData) return true;
  if (body instanceof Blob) return true;
  if (body instanceof URLSearchParams) return true;
  if (body instanceof ArrayBuffer) return true;
  if (ArrayBuffer.isView(body)) return true;
  return false;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;
  const contentType = response.headers.get("content-type") ?? "";
  if (response.headers.get("content-length") === "0") return null;

  const text = await response.text();
  if (text.length === 0) return null;
  if (!contentType.includes("application/json")) return text;
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError("Malformed JSON in response", response.status, text);
  }
}

/**
 * Orval fetch-mutator signature. The generated client calls this as
 * `httpClient<T>(url, { method, headers, body, ... })`, so the signature must
 * accept `(url, RequestInit)` exactly. Hand-written call sites use it the same
 * way (see the note on TD-07 / generated-types in the app README).
 *
 * On every trusted-origin request it attaches: a per-request correlation id,
 * the Firebase Bearer token, and the active `X-Tenant-Id`. A 401 triggers one
 * forced-token-refresh retry to survive a cached-but-stale ID token.
 */
export async function httpClient<TResponse>(
  url: string,
  init: RequestInit = {},
): Promise<TResponse> {
  const method = (init.method ?? "GET").toUpperCase();
  const correlationId = uuidv4();

  const { fullUrl, isTrustedOrigin } = resolveUrl(url);
  const callerHeaders = normalizeHeaders(init.headers);

  const buildHeaders = async (forceRefresh: boolean): Promise<Record<string, string>> => {
    // Framework headers are set last so no caller can strip the Bearer token or
    // the tenant scope. Tenant + auth only go to our trusted origin.
    const framework: Record<string, string> = { "x-request-id": correlationId };
    if (isTrustedOrigin) {
      const tenantId = getActiveTenantId();
      if (tenantId) framework["x-tenant-id"] = tenantId;
    }
    Object.assign(framework, await getAuthHeader(isTrustedOrigin, forceRefresh));
    return { ...callerHeaders, ...framework };
  };

  let headers = await buildHeaders(false);
  let response = await fetch(fullUrl, { ...init, method, headers });

  if (
    response.status === 401 &&
    isTrustedOrigin &&
    firebaseAuth.currentUser &&
    isReplayableBody(init.body)
  ) {
    headers = await buildHeaders(true);
    response = await fetch(fullUrl, { ...init, method, headers });
  }

  const payload = await parseResponseBody(response);

  if (!response.ok) {
    throw new ApiError(`Request failed with status ${response.status}`, response.status, payload);
  }

  return payload as TResponse;
}
